import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { PDFExportRef } from '@axiom/ui';
import {
  useApi,
  PDFExport,
  SubmissionPdf,
  StringUtil,
  CookieUtil,
} from '@axiom/ui';
import { useParams } from 'react-router';

import { DateUtil } from '../../utils/date-util';
import { CandidateApi } from '../../api/candidate-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { EnvUtil } from '../../utils/env-util';
import { useEngagementTalentData } from '../../hooks/useEngagementTalentData';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';

export const PDFWrapper = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
`;

export const SubmissionPdfDownload = ({
  name = 'HIDE_RENDERING',
  onSave,
}: {
  name?: string;
  onSave?: () => void;
}) => {
  const isUserLoggedIn = CookieUtil.hasUserCookie() || null;
  const { accountId, submissionId } = useParams();
  if (!accountId) {
    throw new Error('No accountId provided');
  }
  if (!submissionId) {
    throw new Error('No submissionId provided');
  }

  const pdfRoot = useRef<PDFExportRef>(null);
  const { allCandidateOpps, submission } = useEngagementTalentData();
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const allGroupedExperiences = useApi(
    ...allCandidateOpps.map(({ candidate: { id } }) =>
      CandidateApi.readCandidateGroupedExperiences(id, {
        submissionId,
      })
    )
  ).map(response => response.data);

  const allCandidateIds = allCandidateOpps.map(({ candidate: { id } }) => id);

  const allLegalTechnicalSkills = useApi(
    ...allCandidateOpps.map(({ candidate: { id } }) =>
      isUserLoggedIn
        ? CandidateLegalTechSkillsApi.readLegalTechSkills(id)
        : undefined
    )
  ).map(response => response?.data ?? []);

  const cleanCompanyName = StringUtil.cleanStringForFileName(
    submission?.jobName
  );
  const fileName = `Axiom Matches_${cleanCompanyName}_${DateUtil.formatAsDate(
    new Date()
  )}.pdf`;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const listenerFunction = window.close;
    if (pdfRoot.current?.save) {
      if (onSave) {
        timeout = setTimeout(() => pdfRoot.current?.save(onSave), 500);
      } else {
        window.addEventListener('focus', listenerFunction);

        timeout = setTimeout(() => pdfRoot.current?.save(), 500);
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      window.removeEventListener('focus', listenerFunction);
    };
  }, [pdfRoot]);

  return submission ? (
    <PDFWrapper data-test={name}>
      <PDFExport
        fileName={fileName}
        ref={pdfRoot}
        scale={0.5}
        margin={{ top: '0in', right: '0in', bottom: '0in', left: '0in' }}
      >
        <SubmissionPdf
          isDownloading
          accountId={accountId}
          submission={submission}
          candidateOpps={allCandidateOpps}
          allGroupedExperiences={allGroupedExperiences}
          clientUrl={EnvUtil.clientEngagementsUrl}
          loginUrl={EnvUtil.loginUrl}
          taxonomy={taxonomy}
          allCandidateIds={allCandidateIds}
          allLegalTechnicalSkills={allLegalTechnicalSkills}
        />
      </PDFExport>
    </PDFWrapper>
  ) : null;
};
